import { Component } from '@theme/component';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const OPEN_DELAY = 60;
const CLOSE_DELAY = 160;
const SECTION_DELAY = 80;

/*
 * How far the page has to travel one way before the sticky bar answers, in px. Without a
 * threshold the bar flips on the single pixel a trackpad hands back at the end of a flick, and
 * on the couple of pixels the elastic bounce returns at either end of the page on a phone.
 *
 * Coming back is meant to be cheaper than leaving — the bar should return on a small move up,
 * while leaving reads as a deliberate move down. But a reveal threshold in the single digits
 * sits under the noise rather than above it: inertial scrolling hands back a few pixels upward
 * between frames all the way through a downward flick. At 4px, with 6px rebounds mixed into a
 * downward scroll, the bar came back on 20 passes out of 20 — a bar that never leaves at all.
 *
 * 48 is above that noise and still well under a real gesture: one wheel notch is about 100px,
 * and a short trackpad push is 30 to 60. It is also half the desktop bar height, so the move
 * that brings it back is roughly the move that would have shown half of it anyway.
 */
const HIDE_TRAVEL = 12;
const REVEAL_TRAVEL = 48;

/**
 * DAVIDsTEA header: top level navigation, mega menu panels and inline search.
 *
 * The search field is deliberately absent from these refs. It sits inside
 * `<predictive-search-component>`, and Component hands a `ref` to the closest component
 * ancestor, which is that element and not this one. Read it through `#searchField`.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [burger]
 * @property {HTMLElement} [searchRow]
 * @property {HTMLElement} [megaRoot]
 *
 * @extends {Component<Refs>}
 */
export class DtHeader extends Component {
  /** @type {number | undefined} */
  #openTimer;
  /** @type {number | undefined} */
  #closeTimer;
  /** @type {number | undefined} */
  #sectionTimer;
  /** @type {HTMLElement | null} */
  #activePanel = null;
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  /** @type {number | null} */
  #scrollRaf = null;
  /** Last scroll position answered, in px. */
  #lastScrollTop = 0;
  /** Signed travel since the last change of direction, in px. */
  #travel = 0;
  /** Border-box height of the bar, kept by the observer below. */
  #barHeight = 0;

  /*
   * A ResizeObserver rather than offsetHeight, because the height is wanted on every scroll
   * frame and reading it there would flush layout each time. The observer hands over the same
   * number without asking the page for it, and it fires once on observe, so there is no first
   * value to seed by hand. Border box and not content box: the bar's 1px rule has to clear the
   * top of the window along with the rest of it.
   */
  #barResize = new ResizeObserver(([entry]) => {
    if (!entry) return;
    this.#barHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
  });

  /** Border-box height of the announcement bar above, kept by the observer below. */
  #announcementHeight = 0;

  /*
   * The announcement bar sticks at the top of the window and this bar sticks below it, so the
   * header's `inset-block-start` is the bar's rendered height. That height is not a constant —
   * the message wraps to a second line on a narrow phone, the bar can be absent altogether, and
   * the theme editor can change its copy live — so it is measured rather than written down.
   *
   * Published on the root element and not on this one, because the header is not the only
   * reader: `--sticky-header-offset` is built from it too, and that is what every other sticky
   * element on the page parks under.
   */
  #announcementResize = new ResizeObserver(([entry]) => {
    if (!entry) return;
    this.#announcementHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
    document.documentElement.style.setProperty(
      '--dt-announcement-height',
      `${Math.round(this.#announcementHeight)}px`
    );
  });

  connectedCallback() {
    super.connectedCallback();

    // Gated on the sticky bar itself, not on the hide-on-scroll behaviour: the offset is what
    // holds the header below the announcement bar, and it is wanted whether or not the header
    // is also set to slide away.
    if (this.dataset.sticky === 'true') this.#startAnnouncementOffset();
    if (this.dataset.stickyHide === 'true') this.#startSticky();

    this.#triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', this.#onTriggerEnter);
      trigger.addEventListener('focus', this.#onTriggerFocus);
      trigger.addEventListener('click', this.#onTriggerClick);
    });

    this.addEventListener('pointerleave', this.#scheduleClose);
    this.addEventListener('pointerenter', this.#cancelClose);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('keydown', this.#onKeydown);

    this.#sectionTriggers.forEach((button) => {
      button.addEventListener('click', this.#onSectionTriggerClick);
      button.addEventListener('pointerenter', this.#onSectionTriggerEnter);
      button.addEventListener('pointerleave', this.#cancelSectionSwap);
    });

    this.#searchField?.addEventListener('input', this.#onSearchInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#sectionTimer);
    this.#announcementResize.disconnect();
    this.#stopSticky();
  }

  /* --- Announcement offset ------------------------------------------------ */

  #startAnnouncementOffset() {
    // Rendered by a sibling section, so it is reached through the document rather than through
    // this element. Absent on a theme whose header group carries no announcement bar, and the
    // custom property then keeps the 0px the stylesheet declares.
    const bar = document.querySelector('.dt-announcement');
    if (bar) this.#announcementResize.observe(bar);
  }

  /* --- Sticky reveal ------------------------------------------------------ */

  #startSticky() {
    this.#barResize.observe(this);
    this.#lastScrollTop = Math.max(0, getScrollTop());
    this.#bindScroll();

    // Tabbing back into a bar that has left the window would move focus somewhere nobody can
    // see, and a sticky bar cannot be scrolled back into view. Bring it back instead.
    this.addEventListener('focusin', this.#reveal);
    scrollContainerMediaQuery.addEventListener('change', this.#rebindScroll);

    // Same reason, in the theme editor: selecting a section scrolls to it, and the merchant
    // needs the bar in front of them while they are changing its settings.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#reveal);
      document.addEventListener('shopify:block:select', this.#reveal);
    }
  }

  #stopSticky() {
    this.#barResize.disconnect();
    this.#unbindScroll();
    this.removeEventListener('focusin', this.#reveal);
    scrollContainerMediaQuery.removeEventListener('change', this.#rebindScroll);
    document.removeEventListener('shopify:section:select', this.#reveal);
    document.removeEventListener('shopify:block:select', this.#reveal);

    if (this.#scrollRaf !== null) cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = null;
  }

  #bindScroll() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #unbindScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
  }

  /*
   * The page does not scroll in the window here. Above 990 the scroll container is
   * `.page-wrapper`, below it the document takes over so the phone can hide its address bar,
   * and the theme's scroll-container helper is what knows which. A listener bound at one width
   * is bound to the wrong thing after a resize across 990, and it fails by going silent.
   */
  #rebindScroll = () => {
    this.#unbindScroll();
    this.#bindScroll();
    this.#lastScrollTop = Math.max(0, getScrollTop());
    this.#travel = 0;
    this.#reveal();
  };

  #onScroll = () => {
    if (this.#scrollRaf !== null) return;

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#updateSticky();
    });
  };

  #updateSticky() {
    /*
     * Clamped low: a phone hands back a negative position while the rubber band at the top of
     * the page settles, and a negative reads as a move upwards that nobody made.
     */
    const top = Math.max(0, getScrollTop());
    const delta = top - this.#lastScrollTop;
    this.#lastScrollTop = top;

    /*
     * Nothing leaves in the first screenful. For the first 48px or so the bar is still in the
     * flow under the announcement, and past that it has been pinned for less than its own
     * height, so it stays in sight for a moment before it can go.
     */
    if (top <= this.#barHeight || this.#heldOpen) {
      this.#travel = 0;
      this.#reveal();
      return;
    }

    if (delta === 0) return;

    // The tally counts travel since the last turn, not since the page loaded.
    if ((delta > 0) !== (this.#travel > 0)) this.#travel = 0;
    this.#travel += delta;

    if (this.#travel >= HIDE_TRAVEL) this.#hide();
    else if (this.#travel <= -REVEAL_TRAVEL) this.#reveal();
  }

  /** Whether something on the bar is open or holding focus and has to stay where it is. */
  get #heldOpen() {
    if (this.dataset.searchOpen === 'true') return true;
    if (this.querySelector('[data-panel-trigger][aria-expanded="true"]')) return true;

    // The drawer is rendered beside this element rather than inside it.
    const drawer = document.getElementById('dt-drawer');
    if (drawer instanceof HTMLElement && !drawer.hidden) return true;

    return this.contains(document.activeElement);
  }

  #hide() {
    if (this.dataset.stickyHidden === 'true') return;

    /*
     * The one layout read in the whole loop, and it runs on the hide itself rather than on
     * every scroll frame. It answers what the travel threshold alone cannot: whether the bar
     * has actually reached its sticky rest, which is the announcement bar's lower edge and not
     * the top of the window. Hiding it before it gets there would slide a bar that is still in
     * the flow, and it would jump rather than leave.
     */
    if (this.getBoundingClientRect().top > this.#announcementHeight + 1) return;

    this.dataset.stickyHidden = 'true';
  }

  #reveal = () => {
    delete this.dataset.stickyHidden;
  };

  /** @returns {HTMLAnchorElement[]} */
  get #triggers() {
    return Array.from(this.querySelectorAll('[data-panel-trigger]'));
  }

  /** @returns {HTMLButtonElement[]} */
  get #sectionTriggers() {
    return Array.from(this.querySelectorAll('[data-dt-section-trigger]'));
  }

  #onTriggerEnter = (/** @type {Event} */ event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    this.#cancelClose();
    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => this.#openPanelFor(trigger), OPEN_DELAY);
  };

  #onTriggerFocus = (/** @type {Event} */ event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;
    this.#cancelClose();
    this.#openPanelFor(trigger);
  };

  /**
   * Pointer users get the panel on hover, so a click should follow the link. Keyboard and
   * touch users toggle the panel instead of navigating away from it.
   */
  #onTriggerClick = (/** @type {MouseEvent} */ event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const coarsePointer = window.matchMedia('(hover: none)').matches;
    if (!coarsePointer) return;

    event.preventDefault();
    if (this.#activePanel && this.#activePanel.id === trigger.dataset.panelTrigger) {
      this.#closePanel();
    } else {
      this.#openPanelFor(trigger);
    }
  };

  /** @param {HTMLElement} trigger */
  #openPanelFor(trigger) {
    const id = trigger.dataset.panelTrigger;
    if (!id) return;

    const panel = this.querySelector(`#${CSS.escape(id)}`);
    if (!(panel instanceof HTMLElement) || panel === this.#activePanel) return;

    this.#closePanel();
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.#activePanel = panel;
  }

  #closePanel() {
    if (!this.#activePanel) return;

    const id = this.#activePanel.id;
    this.#activePanel.hidden = true;
    this.#activePanel = null;

    const trigger = this.querySelector(`[data-panel-trigger="${id}"]`);
    trigger?.setAttribute('aria-expanded', 'false');
  }

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
  };

  #scheduleClose = () => {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#closePanel(), CLOSE_DELAY);
  };

  #onFocusOut = (/** @type {FocusEvent} */ event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#closePanel();
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;

    if (this.#activePanel) {
      this.#closePanel();
    } else if (this.dataset.searchOpen === 'true') {
      /*
       * Escape is answered twice on the way down, and the order is fixed: the predictive search
       * component takes it first, from a delegated listener on the document in the capture
       * phase, and empties the field there and then. By the time this one runs the field already
       * reads empty and cannot say whether anything was dismissed — so the term recorded on the
       * last keystroke is what answers. First press drops the suggestions, second press closes
       * the row.
       */
      if (this.#searchTerm !== '') {
        this.#searchTerm = '';
        this.#searchField?.setAttribute('aria-expanded', 'false');
        return;
      }

      this.closeSearch();
    }
  };

  #onSectionTriggerClick = (/** @type {Event} */ event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    this.#showSection(button);
  };

  /**
   * The sidebar answers to the pointer, as the top level triggers already do. The delay is
   * what keeps a diagonal move towards the panel from swapping the stage on its way past.
   * The click above stays for keyboard and touch, where there is no hover to answer to.
   */
  #onSectionTriggerEnter = (/** @type {Event} */ event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    if (window.matchMedia('(hover: none)').matches) return;

    clearTimeout(this.#sectionTimer);
    this.#sectionTimer = setTimeout(() => this.#showSection(button), SECTION_DELAY);
  };

  #cancelSectionSwap = () => {
    clearTimeout(this.#sectionTimer);
  };

  /** @param {HTMLElement} button */
  #showSection(button) {
    const targetId = button.dataset.dtSectionTrigger;
    const panel = button.closest('[data-dt-panel]');
    if (!targetId || !(panel instanceof HTMLElement)) return;

    // Each sidebar entry tints the whole panel, so the colour follows the swap.
    const background = button.dataset.dtSectionBg;
    if (background) panel.style.setProperty('--dt-panel-bg', background);

    panel.querySelectorAll('[data-dt-section]').forEach((view) => {
      if (view instanceof HTMLElement) view.hidden = view.id !== targetId;
    });

    panel.querySelectorAll('[data-dt-section-trigger]').forEach((other) => {
      other.setAttribute('aria-pressed', String(other === button));
    });
  }

  /* --- Inline search ----------------------------------------------------- */

  /**
   * The search field, read from the DOM rather than from refs — see the note on the Refs
   * typedef above.
   *
   * @returns {HTMLInputElement | null}
   */
  get #searchField() {
    const field = this.querySelector('#dt-search-input');
    return field instanceof HTMLInputElement ? field : null;
  }

  /** What the field held at the last keystroke. */
  #searchTerm = '';

  /*
   * The suggestions panel is opened and closed by a CSS rule reading the field, so nothing here
   * shows or hides it. This listener is for the two things CSS cannot do: remember the term for
   * the Escape handling above, and keep the combobox state honest. The predictive search
   * component never touches `aria-expanded` — it ships the attribute hardcoded to false in the
   * stock markup — so this is filling a gap rather than duplicating it.
   */
  #onSearchInput = () => {
    const field = this.#searchField;
    if (!field) return;

    this.#searchTerm = field.value;
    field.setAttribute('aria-expanded', this.#searchTerm.trim() === '' ? 'false' : 'true');
  };

  toggleSearch() {
    if (this.dataset.searchOpen === 'true') {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    const { searchRow } = this.refs;
    if (!searchRow) return;

    this.#closePanel();
    searchRow.hidden = false;
    this.dataset.searchOpen = 'true';
    this.querySelector('.dt-header__search-toggle')?.setAttribute('aria-expanded', 'true');
    this.#searchField?.focus();
  }

  closeSearch() {
    const { searchRow } = this.refs;
    if (!searchRow) return;

    searchRow.hidden = true;
    delete this.dataset.searchOpen;

    const toggle = this.querySelector('.dt-header__search-toggle');
    toggle?.setAttribute('aria-expanded', 'false');
    if (toggle instanceof HTMLElement) toggle.focus();
  }

  /* --- Mobile drawer ------------------------------------------------------ */
  toggleDrawer() {
    const drawer = document.getElementById('dt-drawer');
    if (drawer instanceof DtDrawer) drawer.toggle();
  }
}

/**
 * DAVIDsTEA mobile drawer with drill-down views.
 *
 * @extends {Component}
 */
export class DtDrawer extends Component {
  /** @type {string[]} */
  #history = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  toggle() {
    this.hidden ? this.open() : this.close();
  }

  open() {
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    this.#showView('root');
    this.#history = [];
    this.#syncBurger(true);

    const focusable = this.querySelector('button, a[href]');
    if (focusable instanceof HTMLElement) focusable.focus();
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    this.#syncBurger(false);
  }

  /** @param {boolean} expanded */
  #syncBurger(expanded) {
    document.querySelector('.dt-header__burger')?.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * @param {string} name
   * @param {string} [tabId] - Section to select once the view is shown.
   */
  #showView(name, tabId) {
    this.querySelectorAll('[data-dt-drawer-view]').forEach((view) => {
      if (view instanceof HTMLElement) view.hidden = view.dataset.dtDrawerView !== name;
    });

    const view = this.querySelector(`[data-dt-drawer-view="${CSS.escape(name)}"]`);
    if (!(view instanceof HTMLElement)) return;

    const tab = tabId
      ? view.querySelector(`[data-dt-drawer-tab-trigger="${CSS.escape(tabId)}"]`)
      : view.querySelector('[data-dt-drawer-tab-trigger]');

    if (tab instanceof HTMLElement) this.#selectTab(tab);
    view.querySelector('.dt-drawer__body')?.scrollTo({ top: 0 });
  }

  /** @param {HTMLElement} tab */
  #selectTab(tab) {
    const view = tab.closest('[data-dt-drawer-view]');
    const targetId = tab.dataset.dtDrawerTabTrigger;
    if (!targetId || !(view instanceof HTMLElement)) return;

    // Each tab tints the whole panel, as the desktop sidebar does.
    const background = tab.dataset.dtSectionBg;
    if (background) view.style.setProperty('--dt-panel-bg', background);

    view.querySelectorAll('[data-dt-drawer-section]').forEach((sectionView) => {
      if (sectionView instanceof HTMLElement) sectionView.hidden = sectionView.id !== targetId;
    });

    view.querySelectorAll('[data-dt-drawer-tab-trigger]').forEach((other) => {
      other.setAttribute('aria-selected', String(other === tab));
    });
  }

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-dt-drawer-close]')) {
      this.close();
      return;
    }

    const tab = target.closest('[data-dt-drawer-tab-trigger]');
    if (tab instanceof HTMLElement) {
      this.#selectTab(tab);
      return;
    }

    const opener = target.closest('[data-dt-drawer-open]');
    if (opener instanceof HTMLElement && opener.dataset.dtDrawerOpen) {
      const current = this.querySelector('[data-dt-drawer-view]:not([hidden])');
      if (current instanceof HTMLElement && current.dataset.dtDrawerView) {
        this.#history.push(current.dataset.dtDrawerView);
      }
      this.#showView(opener.dataset.dtDrawerOpen, opener.dataset.dtDrawerTab);
      return;
    }

    if (target.closest('[data-dt-drawer-back]')) {
      this.#showView(this.#history.pop() ?? 'root');
    }
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape') this.close();
  };
}

/*
 * The localization selectors are plain <details> elements, which do not close on an
 * outside click on their own.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  document.querySelectorAll('[data-dt-localization] details[open]').forEach((details) => {
    if (target instanceof Node && details.contains(target)) {
      /*
       * The panel carries its own close button, so the outside-click test above would leave
       * it open. It is the one inside click that closes rather than keeps open.
       */
      if (target instanceof Element && target.closest('[data-dt-loc-close]')) {
        details.removeAttribute('open');
      }
      return;
    }
    details.removeAttribute('open');
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-dt-localization] details[open]').forEach((details) => {
    details.removeAttribute('open');
  });
});

if (!customElements.get('dt-header-component')) {
  customElements.define('dt-header-component', DtHeader);
}

if (!customElements.get('dt-drawer-component')) {
  customElements.define('dt-drawer-component', DtDrawer);
}
